import { useState, useEffect, useCallback, useRef, createContext, useContext, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface FeatureFlag {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface FeatureFlagsContextType {
  flags: FeatureFlag[];
  loading: boolean;
  isAdmin: boolean;
  isFeatureEnabled: (flagName: string) => boolean;
  refetchFlags: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
  flags: [],
  loading: false,
  isAdmin: false,
  isFeatureEnabled: () => false,
  refetchFlags: async () => {},
});

/**
 * Hook to check if a specific feature is enabled.
 * 
 * - Admin users (Bridget) ALWAYS get true — they see everything in beta.
 * - Regular users only see features where enabled = true in the database.
 * - If the flag doesn't exist in the database, it defaults to false for regular users.
 * - If the table doesn't exist yet, everything defaults gracefully (no crash).
 */
export function useFeatureFlag(flagName: string): boolean {
  const context = useContext(FeatureFlagsContext);
  return context.isFeatureEnabled(flagName);
}

/**
 * Hook to access all feature flags and management functions.
 * Used by the Admin Panel's FeatureFlagsTab.
 */
export function useFeatureFlagsContext() {
  return useContext(FeatureFlagsContext);
}

export { FeatureFlagsContext };

/**
 * Provider component — wrap your app with this.
 * Loads all feature flags once on mount and caches them.
 * Gracefully handles missing table — app works fine without it.
 */
/**
 * VA-249 — the last known answer, so a returning member is never asked to wait for it.
 *
 * Bridget: "almost every time we access the chat initially, it shows old options." The cause
 * was that an unknown flag reads as OFF, so the chat decided the feature was disabled before
 * it knew. Guarding the render on `loading` stopped it claiming anything false, but left Kate
 * watching "Getting your skills ready" for 5 to 15 seconds on a slow connection, because the
 * flags are re-fetched on every mount and that request queues behind everything else.
 *
 * So we remember. First visit behaves exactly as before; every visit after renders the right
 * view immediately and corrects itself in the background if an admin changed something.
 *
 * SAFE BECAUSE THE SERVER DOES NOT TRUST THIS. vision-chat enforces assistant_skills itself,
 * in three places, alongside the tier and published checks. A stale ON here shows cards that
 * the server then refuses to run — it cannot grant access to anything.
 *
 * ONLY THE FLAGS ARE STORED. isAdmin is deliberately not cached: a revoked admin would keep
 * their bypass on the next load, and there is nothing to gain by it — the user_roles query has
 * already resolved by the point we restore. Still keyed by user so nothing crosses accounts on
 * a shared browser.
 */
interface FlagSnapshot {
  flags: FeatureFlag[];
}

const SNAPSHOT_PREFIX = "va249-flags:";

/**
 * The signed-in user id, read straight out of storage with no awaits.
 *
 * supabase.auth.getSession() is a promise, and awaiting it — then awaiting the user_roles
 * query after it — is exactly the delay this cache exists to remove. Restoring after those
 * two round trips still showed the placeholder, just for less time; the e2e spec caught it.
 * The session is already sitting in localStorage, so read it there and seed on the very
 * first render instead. Same approach as storedSession() in src/lib/blankScreenWatch.ts.
 */
function currentUserIdSync(): string | null {
  try {
    const key = Object.keys(localStorage).find((k) => /^sb-.*-auth-token$/.test(k));
    if (!key) return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const id = parsed?.user?.id;
    return typeof id === "string" ? id : null;
  } catch {
    return null;
  }
}

function readSnapshot(userId: string): FlagSnapshot | null {
  // Private windows throw on access. A cache that breaks the app it is speeding up is worse
  // than no cache, so every path here fails silently back to "we do not know yet".
  try {
    const raw = localStorage.getItem(SNAPSHOT_PREFIX + userId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FlagSnapshot;
    if (!Array.isArray(parsed?.flags)) return null;
    return { flags: parsed.flags };
  } catch {
    return null;
  }
}

function writeSnapshot(userId: string, snap: FlagSnapshot): void {
  try {
    localStorage.setItem(SNAPSHOT_PREFIX + userId, JSON.stringify(snap));
  } catch { /* storage full or blocked — the next load simply waits, as it does today */ }
}

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  // Seeded before the first paint, so a member who has been here before never waits. Lazy
  // initialisers, so the storage read happens once rather than on every render.
  const [flags, setFlags] = useState<FeatureFlag[]>(
    () => readSnapshot(currentUserIdSync() ?? "")?.flags ?? [],
  );
  const [loading, setLoading] = useState(
    () => readSnapshot(currentUserIdSync() ?? "") === null,
  );
  const [isAdmin, setIsAdmin] = useState(false);

  // Which identity the values in state belong to. `undefined` means nothing has loaded yet,
  // `null` means signed out. Every response is checked against this before it is applied, so
  // a slow reply for a previous identity cannot land on top of a newer one — sign out and
  // straight back in is fast enough for that to happen.
  const loadedFor = useRef<string | null | undefined>(undefined);

  /**
   * Load everything for one identity: the admin role and the flags, together.
   *
   * Both used to be separate effects that ran once on mount. That is the bug Kate found —
   * see the auth subscription below — and it is also why `loading` did not use to cover the
   * role query, so an admin could be told a feature was off before their role was known.
   * One function for one identity removes both problems at once.
   */
  const loadFor = useCallback(async (uid: string | null) => {
    loadedFor.current = uid;

    // What we learned last time about THIS user, so a returning member never waits.
    const snap = uid ? readSnapshot(uid) : null;
    if (snap) {
      setFlags(snap.flags);
      setLoading(false);
    } else {
      setLoading(true);
    }

    const [roleRes, flagRes] = await Promise.allSettled([
      uid
        ? supabase.from("user_roles").select("role").eq("user_id", uid).eq("role", "admin").maybeSingle()
        : Promise.resolve({ data: null, error: null }),
      supabase.from("feature_flags").select("*").order("created_at", { ascending: true }),
    ]);

    // Somebody else signed in while we were waiting. Their load owns the state now.
    if (loadedFor.current !== uid) return;

    setIsAdmin(
      roleRes.status === "fulfilled" && !!roleRes.value?.data && !roleRes.value?.error,
    );

    if (flagRes.status === "fulfilled" && !flagRes.value.error) {
      const fresh = (flagRes.value.data as FeatureFlag[]) || [];
      setFlags(fresh);
      // Remembered only for a signed-in user. Signed out, RLS returns an empty list, and
      // caching that would teach the app that every feature is off.
      if (uid) writeSnapshot(uid, { flags: fresh });
    } else if (!snap) {
      // Nothing cached and the fetch failed — better to show nothing than to invent state.
      setFlags([]);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) =>
      loadFor(data?.session?.user?.id ?? null),
    );

    // VA-249, Kate: "when I signed out and then signed back in, the banner appeared."
    //
    // The flags were fetched once, on mount, and never again. Signing out fetches them with
    // no session — and RLS returns an empty list to an anonymous reader, so every feature
    // reads as off. Signing back in is a client-side transition: the provider does not
    // remount, nothing refetches, and that empty list stays. The app then tells a member
    // with Skills that they are "coming in the next update".
    //
    // Only a change of identity matters. onAuthStateChange also fires on every token
    // refresh, and refetching on those would put the app back on the network every hour for
    // an answer it already has.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const uid = session?.user?.id ?? null;
      if (uid === loadedFor.current) return;
      void loadFor(uid);
    });
    return () => sub.subscription.unsubscribe();
  }, [loadFor]);

  // Used by the admin Feature Flags tab after it toggles something, so the change is
  // reflected without a reload. Reloads for whoever is signed in now, which also refreshes
  // the stored snapshot — otherwise the next visit would seed from the value just replaced.
  const refetchFlags = useCallback(
    () => loadFor(loadedFor.current ?? null),
    [loadFor],
  );

  // Check if a feature is enabled
  const isFeatureEnabled = useCallback(
    (flagName: string): boolean => {
      // Admin always sees everything — this is the beta mode
      if (isAdmin) return true;

      // For regular users, check the flag
      const flag = flags.find((f) => f.name === flagName);
      if (!flag) return false; // Flag doesn't exist = not enabled
      return flag.enabled;
    },
    [flags, isAdmin]
  );

  const contextValue: FeatureFlagsContextType = {
    flags,
    loading,
    isAdmin,
    isFeatureEnabled,
    refetchFlags,
  };

  return (
    <FeatureFlagsContext.Provider value={contextValue}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}
